import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { objectifs as api } from '../services/api';
import { useBi } from '../BiContext';
import './Objectifs.css';
import './Stock.css';

// ─── Objectifs — page unique (VN, VO, APV, PR) ───────────────────────────────
// Remplace « Objectifs APV » (2026-09-30). Trois choix en tête — métier,
// concession, année — puis la même grille pour tous : 12 mois en lignes, les
// indicateurs SAISIS du métier en colonnes, les indicateurs DÉDUITS en grisé.
//
//   VN, VO : volume, CA, marge          → taux de marge, marge par véhicule
//   APV    : CA M.O., heures, CA pièces → taux horaire, CA atelier
//   PR     : CA, taux de marge (imposé) → marge
//
// Saisie mois par mois (décision utilisateur : pas de répartition d'un annuel).
// Une case VIDE suit le budget, affiché en filigrane : on ne renseigne que ce
// qu'on veut piloter. Chaque révision est horodatée et attribuée.

const METIERS = [
  { c: 'VN', l: 'VN' }, { c: 'VO', l: 'VO' }, { c: 'APV', l: 'APV' }, { c: 'PR', l: 'PR' },
];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const nf = new Intl.NumberFormat('fr-FR');
const fmt = (v, u) => {
  if (v == null || Number.isNaN(v)) return '—';
  if (u === 'pct') return `${nf.format(Math.round(v * 10) / 10)} %`;
  if (u === 'eur') return `${nf.format(Math.round(v))} €`;
  if (u === 'eurh') return `${nf.format(Math.round(v))} €/h`;
  return nf.format(Math.round(v));
};
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '');
const div = (a, b) => (a != null && b ? a / b : null);

// Indicateurs déduits, calculés sur les valeurs EFFECTIVES (saisie, à défaut budget).
const DEDUITS = {
  VN: [
    { l: 'Taux de marge', u: 'pct', f: (e) => (div(e.marge, e.ca) ?? null) != null ? div(e.marge, e.ca) * 100 : null },
    { l: 'Marge / véhicule', u: 'eur', f: (e) => div(e.marge, e.volume) },
  ],
  VO: [
    { l: 'Taux de marge', u: 'pct', f: (e) => (div(e.marge, e.ca) ?? null) != null ? div(e.marge, e.ca) * 100 : null },
    { l: 'Marge / véhicule', u: 'eur', f: (e) => div(e.marge, e.volume) },
  ],
  APV: [
    { l: 'Taux horaire', u: 'eurh', f: (e) => div(e.ca_mo, e.heures) },
    { l: 'CA atelier', u: 'eur', f: (e) => (e.ca_mo != null || e.ca_pieces != null ? (e.ca_mo || 0) + (e.ca_pieces || 0) : null) },
  ],
  PR: [
    { l: 'Marge', u: 'eur', f: (e) => (e.ca != null && e.taux_marge != null ? (e.ca * e.taux_marge) / 100 : null) },
  ],
};
// Totaux de l'année des déduits : recalculés depuis les sommes, jamais moyennés.
const TOTAL_DEDUIT = {
  VN: (t) => [div(t.marge, t.ca) != null ? div(t.marge, t.ca) * 100 : null, div(t.marge, t.volume)],
  VO: (t) => [div(t.marge, t.ca) != null ? div(t.marge, t.ca) * 100 : null, div(t.marge, t.volume)],
  APV: (t) => [div(t.ca_mo, t.heures), (t.ca_mo || 0) + (t.ca_pieces || 0)],
  PR: (t) => [t.marge_pr],
};

// Libellé court d'une société : « IMAC2 · Hyundai Cannes/Nice/Villeneuve »
// → « IMAC2 » (nom complet en infobulle) ; vingt boutons doivent tenir.
const court = (nom) => (nom || '').split(' · ')[0];

export default function Objectifs() {
  const { data, branche: brancheCtx } = useBi();
  // Auto Métropole (2026-09-30) : même page, sur les sociétés de la branche
  // courante. Pas de budget en métropole : une case vide n'y vaut rien.
  const branche = brancheCtx === 'auto-metropole' ? 'auto-metropole' : 'auto-antilles';
  const avecBudget = branche === 'auto-antilles';
  const nomBranche = data?.branches?.find((b) => b.code === branche)?.nom || '';
  const [params, setParams] = useSearchParams();
  // Seuls les blocs de l'habilitation (Administration ▸ Accès) : les autres
  // répondraient « accès refusé ».
  const metiersOk = METIERS.filter((m) => data.metiers.some((x) => x.code === m.c.toLowerCase()));
  const metier = metiersOk.some((m) => m.c === params.get('metier')) ? params.get('metier') : (metiersOk[0]?.c || 'VN');
  const anneeCourante = new Date().getFullYear();
  const moisCourant = new Date().getMonth() + 1;

  const societes = useMemo(
    () => (data?.societes || []).filter((s) => s.branche_code === branche && (s.codes_source || []).length), [data, branche]);
  const [codsoc, setCodsoc] = useState('');
  const [annee, setAnnee] = useState(anneeCourante);
  const [g, setG] = useState(null);           // réponse serveur
  const [saisie, setSaisie] = useState({});   // { mois: { indicateur: 'texte' } }
  const [erreur, setErreur] = useState('');
  const [etat, setEtat] = useState('');

  // La société retenue doit appartenir à la branche : changer de branche dans
  // le menu repart de la première société de la nouvelle branche.
  useEffect(() => {
    if (societes.length && !societes.some((s) => s.codes_source.includes(codsoc))) setCodsoc(societes[0].codes_source[0]);
  }, [societes, codsoc]);

  const charger = useCallback(async () => {
    if (!codsoc) return;
    setErreur(''); setEtat('');
    try {
      const { data: d } = await api.grille(codsoc, annee, metier);
      setG(d); setSaisie({});
    } catch (e) { setErreur(e.response?.data?.error || 'Chargement impossible'); setG(null); }
  }, [codsoc, annee, metier]);
  useEffect(() => { charger(); }, [charger]);

  const inds = g?.indicateurs || [];
  const texte = (m, c) => {
    const s = saisie[m.mois]?.[c];
    if (s !== undefined) return s;
    const v = m.valeurs[c].saisi;
    return v == null ? '' : String(inds.find((i) => i.c === c)?.unite === 'pct' ? Math.round(v * 10) / 10 : Math.round(v));
  };
  const modifie = (m, c) => {
    const s = saisie[m.mois]?.[c];
    if (s === undefined) return false;
    const v = m.valeurs[c].saisi;
    const ref = v == null ? '' : String(inds.find((i) => i.c === c)?.unite === 'pct' ? Math.round(v * 10) / 10 : Math.round(v));
    return s.trim().replace(',', '.') !== ref;
  };
  // Valeur effective d'une case : la saisie, à défaut le budget.
  const effectif = (m, c) => {
    const t = texte(m, c).trim().replace(',', '.');
    if (t !== '') return Number(t);
    return m.valeurs[c].budget;
  };
  const effectifs = (m) => Object.fromEntries(inds.map(({ c }) => [c, effectif(m, c)]));
  const nbModifies = g ? g.mois.filter((m) => inds.some(({ c }) => modifie(m, c))).length : 0;
  const maj = (mois, c, v) => setSaisie((s) => ({ ...s, [mois]: { ...s[mois], [c]: v } }));
  const changerMetier = (c) => {
    if (nbModifies && !window.confirm('Des modifications ne sont pas enregistrées. Changer de métier les abandonne. Continuer ?')) return;
    const p = new URLSearchParams(params); p.set('metier', c); setParams(p, { replace: true });
  };
  const changer = (f) => {
    if (nbModifies && !window.confirm('Des modifications ne sont pas enregistrées. Continuer les abandonne. Continuer ?')) return;
    f();
  };

  // « Reprendre le budget » : vide les cases saisies. Le serveur supprime les
  // objectifs et la vue repasse sur le budget.
  const reprendreBudget = () => {
    const s = {};
    g.mois.forEach((m) => inds.forEach(({ c }) => { if (m.valeurs[c].saisi != null) { s[m.mois] = { ...s[m.mois], [c]: '' }; } }));
    setSaisie(s); setEtat('');
  };

  const enregistrer = async () => {
    setErreur(''); setEtat('Enregistrement…');
    try {
      const corps = g.mois.filter((m) => inds.some(({ c }) => modifie(m, c))).map((m) => ({
        mois: m.mois,
        valeurs: Object.fromEntries(inds.map(({ c }) => {
          const t = texte(m, c).trim().replace(',', '.');
          return [c, t === '' ? null : Number(t)];
        })),
      }));
      const { data: d } = await api.enregistrer(codsoc, annee, metier, corps);
      setG((x) => ({ ...x, mois: d.mois })); setSaisie({});
      setEtat(d.ecrits ? `${d.ecrits} valeur${d.ecrits > 1 ? 's' : ''} enregistrée${d.ecrits > 1 ? 's' : ''}` : 'Aucun changement');
    } catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); setEtat(''); }
  };

  // Totaux de l'année : sommes des valeurs effectives ; un taux se recalcule.
  const totaux = useMemo(() => {
    if (!g) return null;
    const t = {};
    let margePr = null;
    g.mois.forEach((m) => {
      const e = effectifs(m);
      inds.forEach(({ c, unite }) => { if (unite !== 'pct' && e[c] != null) t[c] = (t[c] || 0) + e[c]; });
      if (metier === 'PR' && e.ca != null && e.taux_marge != null) margePr = (margePr || 0) + (e.ca * e.taux_marge) / 100;
    });
    if (metier === 'PR') { t.marge_pr = margePr; t.taux_marge = div(margePr, t.ca) != null ? div(margePr, t.ca) * 100 : null; }
    return t;
  }, [g, saisie]); // eslint-disable-line react-hooks/exhaustive-deps

  const nomSoc = societes.find((s) => (s.codes_source || []).includes(codsoc))?.nom || codsoc;
  const editable = g?.editable;
  const deduits = DEDUITS[metier];

  return (
    <main className="ob-page">
      <div className="ob-head">
        <h1>Objectifs{nomBranche ? ` · ${nomBranche}` : ''}</h1>
        <p className="ob-sub">
          Saisie mois par mois. {avecBudget
            ? <><b>Une case vide suit le budget</b>, affiché en filigrane : on ne renseigne que ce qu'on veut piloter.</>
            : <><b>Aucun budget n'est chargé pour cette branche</b> : une case vide n'a pas d'objectif, et la jauge d'accueil se compare alors au mois précédent (ou à N-1 en cumul).</>}
          {' '}Les valeurs grisées se calculent.{metier === 'PR' ? ' Pour les pièces, le taux de marge est imposé et la marge en découle.' : ''}
        </p>
      </div>

      <div className="ob-card">
        <div className="ob-card-h ob-choix">
          <span className="st-seg">{metiersOk.map((m) => (
            <button key={m.c} type="button" className={metier === m.c ? 'on' : ''} onClick={() => changerMetier(m.c)}>{m.l}</button>
          ))}</span>
          <span className={`st-seg${societes.length > 6 ? ' ob-socs' : ''}`}>{societes.map((s) => (
            <button key={s.code} type="button" className={s.codes_source.includes(codsoc) ? 'on' : ''} title={s.nom}
              onClick={() => changer(() => setCodsoc(s.codes_source[0]))}>{court(s.nom)}</button>
          ))}</span>
          <span className="st-seg">{[anneeCourante - 1, anneeCourante, anneeCourante + 1].map((a) => (
            <button key={a} type="button" className={annee === a ? 'on' : ''} onClick={() => changer(() => setAnnee(a))}>{a}</button>
          ))}</span>
        </div>
        <div className="ob-card-h">
          <h2>{g?.nom || ''} · {nomSoc} · {annee}</h2>
          {editable && (
            <div className="ob-tools">
              {avecBudget && <button className="ob-btn" onClick={reprendreBudget}>Reprendre le budget</button>}
              <button className="ob-btn ob-btn-p" onClick={enregistrer} disabled={!nbModifies}>
                Enregistrer{nbModifies ? ` (${nbModifies} mois)` : ''}
              </button>
            </div>
          )}
        </div>

        {erreur && <div className="ob-erreur">{erreur}</div>}
        {!g ? <div className="ob-vide">Chargement…</div> : (
          <>
            <div className="ob-scroll">
              <table className="ob-tbl">
                <thead><tr>
                  <th className="l">Mois</th>
                  {inds.map((i) => <th key={i.c}>{i.lib}<small>{i.unite === 'pct' ? 'objectif, en %' : 'objectif'}</small></th>)}
                  {deduits.map((d) => <th key={d.l}>{d.l}<small>déduit</small></th>)}
                  <th className="l">Dernière révision</th>
                </tr></thead>
                <tbody>
                  {g.mois.map((m) => {
                    const e = effectifs(m);
                    const rev = m.revisions[0];
                    const passe = annee < anneeCourante || (annee === anneeCourante && m.mois < moisCourant);
                    const courant = annee === anneeCourante && m.mois === moisCourant;
                    const saisiUn = inds.some(({ c }) => m.valeurs[c].saisi != null);
                    return (
                      <tr key={m.mois} className={courant ? 'ob-courant' : passe ? 'ob-passe' : ''}>
                        <td className="l ob-mois">
                          {MOIS[m.mois - 1]}
                          {courant && <span className="ob-badge ob-b-cours">en cours</span>}
                          {saisiUn && <span className="ob-badge ob-b-obj">objectif</span>}
                        </td>
                        {inds.map(({ c, unite }) => {
                          const v = m.valeurs[c];
                          return (
                            <td key={c}>
                              <input type="text" inputMode="decimal" className={modifie(m, c) ? 'ob-modifie' : ''}
                                value={texte(m, c)} disabled={!editable}
                                placeholder={v.budget == null ? '' : (unite === 'pct' ? nf.format(Math.round(v.budget * 10) / 10) : nf.format(Math.round(v.budget)))}
                                onChange={(ev) => maj(m.mois, c, ev.target.value)} />
                              <span className="ob-budget">
                                {avecBudget ? (v.budget == null ? 'pas de budget' : `budget ${fmt(v.budget, unite)}`) : ''}
                                {v.n1 != null ? `${avecBudget ? ' · ' : ''}${annee - 1} : ${fmt(v.n1, unite)}` : ''}
                              </span>
                            </td>
                          );
                        })}
                        {deduits.map((d) => <td key={d.l} className="ob-calc">{fmt(d.f(e), d.u)}</td>)}
                        <td className="l ob-rev">
                          {rev ? <>révisé le {dateFr(rev.le)} · {rev.par}{m.revisions.length > 1 ? ` · ${m.revisions.length} révisions` : ''}</>
                            : <span className="ob-faint">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td className="l"><b>Année</b></td>
                  {inds.map(({ c, unite }) => <td key={c}><b>{fmt(unite === 'pct' ? totaux?.taux_marge : totaux?.[c], unite)}</b></td>)}
                  {TOTAL_DEDUIT[metier](totaux || {}).map((v, i) => <td key={i}><b>{fmt(v, deduits[i].u)}</b></td>)}
                  <td />
                </tr></tfoot>
              </table>
            </div>
            <p className="ob-note">
              {!editable ? 'Lecture seule : la saisie des objectifs est réservée aux administrateurs.'
                : nbModifies ? <><b className="ob-accent">{nbModifies} mois modifié{nbModifies > 1 ? 's' : ''}</b> — non enregistré{nbModifies > 1 ? 's' : ''}.</>
                  : (etat || (avecBudget ? 'Une valeur vidée revient au budget. Le budget lui-même n\'est jamais modifié par cet écran.' : 'Une valeur vidée supprime l\'objectif du mois.'))}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
